import { useCallback, useRef, useState } from 'react';
import { useMedplum } from '@medplum/react';
import {
  ServiceRequest,
  Specimen,
} from '@medplum/fhirtypes';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { createReference, resolveId } from '@medplum/core';

interface UseLinkSpecimensProps {
  serviceRequest: ServiceRequest | undefined;
  requiredTypeCode: string | undefined;
  onSuccess: () => Promise<void>;
  onClose: () => void;
}

export function useLinkSpecimens({
  serviceRequest,
  requiredTypeCode,
  onSuccess,
  onClose,
}: UseLinkSpecimensProps) {
  const medplum = useMedplum();
  const { t } = useTranslation(['lims', 'common']);
  const [linkingLoading, setLinkingLoading] = useState(false);
  const isLinkingRef = useRef(false);

  const linkSpecimens = useCallback(
    async (selectedSpecimens: Specimen[]) => {
      if (!serviceRequest) {
        return;
      }

      // Prevent concurrent submissions (e.g., double-clicks)
      if (isLinkingRef.current || linkingLoading) {
        return;
      }

      // Validate sample type matches order requirement (ValueSet code)
      const mismatched = selectedSpecimens.filter((s) => {
        const specimenType = s.type?.coding?.[0]?.code;
        return requiredTypeCode && specimenType !== requiredTypeCode;
      });
      if (mismatched.length > 0) {
        notifications.show({
          title: t('error', { ns: 'common' }),
          message: t('orderDetails.validation.typeMismatch', { ns: 'lims' }),
          color: 'red',
        });
        return;
      }

      setLinkingLoading(true);
      isLinkingRef.current = true;

      try {
        const serviceRequestRef = createReference(serviceRequest);

        // Get currently linked specimen IDs from ServiceRequest
        const currentLinkedIds = new Set(
          serviceRequest?.specimen?.map((ref) => resolveId(ref)).filter(Boolean)
        );

        // Get selected specimen IDs
        const selectedIds = new Set(
          selectedSpecimens.map((s) => s.id).filter(Boolean)
        );

        // Find specimens to unlink (currently linked but not in selection)
        const idsToUnlink = Array.from(currentLinkedIds).filter(
          (id) => !selectedIds.has(id)
        );

        // Find specimens to link (in selection but not currently linked)
        const specimensToLink = selectedSpecimens.filter(
          (s) => s.id && !currentLinkedIds.has(s.id)
        );

        // Unlink specimens: remove ServiceRequest reference from Specimen.request
        if (idsToUnlink.length > 0) {
          const unlinkPromises = idsToUnlink
            .filter((id): id is string => Boolean(id))
            .map(async (specimenId) => {
              try {
                const specimen = await medplum.readResource(
                  'Specimen',
                  specimenId
                );
                const updatedRequests = (specimen.request || []).filter(
                  (ref) => ref.reference !== serviceRequestRef.reference
                );
                await medplum.updateResource({
                  ...specimen,
                  request: updatedRequests,
                });
              } catch (error) {
                console.error(
                  `Failed to unlink specimen ${specimenId}:`,
                  error
                );
              }
            });
          await Promise.all(unlinkPromises);
        }

        // Link new specimens: add ServiceRequest reference to Specimen.request
        if (specimensToLink.length > 0) {
          const linkPromises = specimensToLink.map((specimen) =>
            medplum.updateResource({
              ...specimen,
              request: [...(specimen.request || []), serviceRequestRef],
            })
          );
          await Promise.all(linkPromises);
        }

        // Update ServiceRequest.specimen array to match selection
        const specimenReferences = selectedSpecimens.map((specimen) => ({
          reference: `Specimen/${specimen.id}`,
          display: specimen.identifier?.[0]?.value || specimen.id,
        }));

        const updatedServiceRequest = {
          ...serviceRequest,
          specimen: specimenReferences,
        };

        await medplum.updateResource(updatedServiceRequest);

        // Reload order details to get updated specimen list
        await onSuccess();

        notifications.show({
          title: t('success', { ns: 'common' }),
          message: t('orderDetails.specimensLinked', { ns: 'lims' }),
          color: 'green',
        });

        onClose();
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : t('orderDetails.specimensLinkError', { ns: 'lims' });
        notifications.show({
          title: t('error', { ns: 'common' }),
          message: errorMessage,
          color: 'red',
        });
      } finally {
        setLinkingLoading(false);
        isLinkingRef.current = false;
      }
    },
    [
      serviceRequest,
      requiredTypeCode,
      linkingLoading,
      medplum,
      t,
      onSuccess,
      onClose,
    ]
  );

  return {
    linkSpecimens,
    linkingLoading,
  };
}
